import { defineStore } from "pinia";
import { pageType } from "@/utils/point";
import { viewPage, viewPageTime } from "@/utils/point";

interface PageTimeState {
  currentPage: pageType | null;
  currentPageEventValue: string | undefined; // 当前页面的参数信息
  enterTime: number;
  reportTimer: number | null; // 定时器ID
  lastReportTime: number; // 上次上报时间
  pausedTime: number; // 暂停的累计时间（毫秒）
  isPaused: boolean; // 是否处于暂停状态
}

export const usePageTimeStore = defineStore("pageTime", {
  state: (): PageTimeState => ({
    currentPage: null,
    currentPageEventValue: undefined,
    enterTime: 0,
    reportTimer: null,
    lastReportTime: Date.now(),
    pausedTime: 0,
    isPaused: false,
  }),

  actions: {
    // 记录进入新页面
    enterPage(page: pageType, eventValue?: string) {
      // 清除旧的定时器
      this.clearReportTimer();

      // 如果已有当前页面，说明是路由切换，需要上报上一个页面的增量时间
      // 注意：这里会立即上报上一个页面从上次上报到现在的增量时间
      if (this.currentPage && this.lastReportTime) {
        const now = Date.now();
        const incrementalTime = now - this.lastReportTime;
        // 只有当增量时间大于等于1秒时才上报，避免上报极小的时间
        if (incrementalTime >= 1000) {
          viewPageTime(
            this.currentPage,
            incrementalTime,
            this.currentPageEventValue
          );
          this.lastReportTime = now;
        }
      }

      // 记录新页面信息
      this.currentPage = page;
      this.currentPageEventValue = eventValue;
      this.enterTime = Date.now();
      this.lastReportTime = Date.now();
      this.pausedTime = 0;
      this.isPaused = false;

      // 上报view_page
      viewPage(page, eventValue);

      // 启动定时上报（每10秒上报一次）
      this.startReportTimer();
    },

    // 启动定时上报（每10秒上报一次）
    startReportTimer() {
      // 清除可能存在的旧定时器
      this.clearReportTimer();

      // 注意：lastReportTime 已经在 enterPage 中设置，这里不再重复设置
      // 设置定时器，每10秒上报一次增量时间
      this.reportTimer = window.setInterval(() => {
        if (!this.isPaused) {
          this.reportIncrementalTime();
        }
      }, 10000); // 10秒 = 10000毫秒
    },

    // 清除定时器
    clearReportTimer() {
      if (this.reportTimer !== null) {
        clearInterval(this.reportTimer);
        this.reportTimer = null;
      }
    },

    // 暂停计时（页面隐藏时）
    pauseTimer() {
      if (this.isPaused) return;
      this.isPaused = true;
      // 记录暂停时的时间点
      this.pausedTime = Date.now();
    },

    // 恢复计时（页面显示时）
    resumeTimer() {
      if (!this.isPaused) return;
      this.isPaused = false;
      // 计算暂停的时长，并调整进入时间和上次上报时间
      if (this.pausedTime > 0) {
        const now = Date.now();
        const pauseDuration = now - this.pausedTime;
        this.enterTime += pauseDuration;
        // 调整上次上报时间，避免暂停期间被计入增量时间
        this.lastReportTime += pauseDuration;
        this.pausedTime = 0;

        // 重置定时器，确保下次触发是在恢复后的10秒，而不是立即触发
        this.startReportTimer();
      }
    },

    // 上报增量时间（每10秒的增量，用于定时上报）
    reportIncrementalTime() {
      if (!this.currentPage || !this.lastReportTime) return;
      const now = Date.now();
      const incrementalTime = now - this.lastReportTime;
      viewPageTime(
        this.currentPage,
        incrementalTime,
        this.currentPageEventValue
      );
      this.lastReportTime = now;
    },
  },
});
